/**
 * Utility functions for ticket-related functionality
 */
import { TICKET_TYPES, PRICING_TYPES } from "../constants/events"

/**
 * Calculate if tickets are available
 * @param {number} maxTickets - Maximum number of tickets (0 for unlimited)
 * @param {number} ticketsSold - Number of tickets already sold
 * @returns {boolean} - True if tickets are available
 */
export const areTicketsAvailable = (maxTickets, ticketsSold) => {
  return maxTickets === 0 || maxTickets - ticketsSold > 0
}

/**
 * Calculate remaining tickets
 * @param {number} maxTickets - Maximum number of tickets
 * @param {number} ticketsSold - Number of tickets already sold
 * @returns {number} - Number of tickets remaining
 */
export const getTicketsRemaining = (maxTickets, ticketsSold) => {
  return maxTickets - ticketsSold
}

/**
 * Check if event is over
 * @param {string|Date} eventEndDate - Event end date
 * @returns {boolean} - True if event has ended
 */
export const isEventOver = (eventEndDate) => {
  if (!eventEndDate) return false
  return new Date(eventEndDate) < new Date()
}

/**
 * Get ticket availability message with low ticket warning
 * @param {number} maxTickets - Maximum number of tickets (0 for unlimited)
 * @param {number} ticketsSold - Number of tickets already sold
 * @param {string|Date} eventEndDate - Event end date
 * @returns {string} - Availability message
 */
export const getTicketAvailabilityMessage = (
  maxTickets,
  ticketsSold,
  eventEndDate,
) => {
  if (isEventOver(eventEndDate)) {
    return "Event ended"
  }

  // Unlimited (maxTickets === 0) has nothing worth announcing — no message.
  if (maxTickets === 0) {
    return null
  }

  const ticketsRemaining = getTicketsRemaining(maxTickets, ticketsSold)

  // Check if remaining tickets are less than 20% of max tickets
  const lowTicketThreshold = maxTickets * 0.2
  if (ticketsRemaining <= lowTicketThreshold && ticketsRemaining > 0) {
    return "Low ticket warning!"
  }

  return null
}

/**
 * Check if low ticket warning should be shown
 * @param {number} maxTickets - Maximum number of tickets
 * @param {number} ticketsSold - Number of tickets already sold
 * @returns {boolean} - True if low ticket warning should be shown
 */
export const shouldShowLowTicketWarning = (maxTickets, ticketsSold) => {
  if (maxTickets === 0) return false

  const ticketsRemaining = getTicketsRemaining(maxTickets, ticketsSold)
  const lowTicketThreshold = maxTickets * 0.2

  return ticketsRemaining <= lowTicketThreshold && ticketsRemaining > 0
}

export const formatPrice = (price) => {
  if (price == null) return null
  const n = parseFloat(String(price))
  if (isNaN(n)) return null
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`
}

// Mirrors the backend's TieredPlatformFee constants (app/models/concerns/tiered_platform_fee.rb).
// Change the tiered fee schedule here only - calculateBandShareForTicketPrice
// and calculateGrossPriceForBandShare both derive from these, nowhere else.
export const TICKET_PRICE_MAX_NO_FEE = 5
export const TICKET_PRICE_MAX_FLAT_FEE = 15
export const FLAT_FEE = 1
export const PERCENTAGE_FEE = 10

// Mirrors the backend's TieredPlatformFee#calculate_standard_shares_for_amount
// formula for bandNada tickets.
export const calculateBandShareForTicketPrice = (price) => {
  const n = parseFloat(String(price))
  if (isNaN(n) || n <= 0) return 0
  if (n <= TICKET_PRICE_MAX_NO_FEE) return n
  if (n <= TICKET_PRICE_MAX_FLAT_FEE) return n - FLAT_FEE
  return Math.round(n * (1 - PERCENTAGE_FEE / 100) * 100) / 100
}

// Inverse of calculateBandShareForTicketPrice - mirrors the backend's
// gross_up_amount_for_band_share, for live preview only. The actual stored
// ticket_price is computed server-side at creation (Post#apply_pre_purchase_fee_gross_up).
export const calculateGrossPriceForBandShare = (bandShare) => {
  const n = parseFloat(String(bandShare))
  if (isNaN(n) || n <= 0) return 0
  if (n <= TICKET_PRICE_MAX_NO_FEE) return n
  const maxBandShareAtFlatFee = TICKET_PRICE_MAX_FLAT_FEE - FLAT_FEE
  if (n <= maxBandShareAtFlatFee) return n + FLAT_FEE
  return Math.round((n / (1 - PERCENTAGE_FEE / 100)) * 100) / 100
}

export const getTicketPriceDisplay = (concert) => {
  if (concert.ticket_type === TICKET_TYPES.BN_TICKETS.value) {
    if (concert.pricing_type === PRICING_TYPES.FREE.value) return "Free"
    if (concert.pricing_type === PRICING_TYPES.SLIDING_SCALE.value) {
      const min = formatPrice(concert.ticket_price)
      const max = formatPrice(concert.ticket_price_max)
      return min && max ? `${min}–${max}` : null
    }
    return formatPrice(concert.ticket_price)
  }
  const isRsvp = concert.ticket_type === TICKET_TYPES.NO_ADVANCE_TICKETS.value
  const isExternalTickets =
    concert.ticket_type === TICKET_TYPES.EXTERNAL_TICKETS.value ||
    (!!concert.ticket_link_url &&
      concert.ticket_type !== TICKET_TYPES.BN_TICKETS.value)
  if (!isRsvp && !isExternalTickets) return null
  if (
    !concert.pricing_type ||
    concert.pricing_type === PRICING_TYPES.FREE.value
  )
    return "Free"
  const atDoor = isRsvp ? " at door" : ""
  if (concert.pricing_type === PRICING_TYPES.SLIDING_SCALE.value) {
    const min = formatPrice(concert.ticket_price)
    const max = formatPrice(concert.ticket_price_max)
    return min && max ? `${min}–${max}${atDoor}` : null
  }
  const price = formatPrice(concert.ticket_price)
  return price ? `${price}${atDoor}` : null
}
